/**
 * The observation — one agent's fixed-width, normalized, MIRRORED view of the
 * world.
 *
 * ===========================================================================
 * 1. EVERY FIELD IS DIVIDED BY A STATED SCALE
 * ===========================================================================
 *
 * A raw observation mixes a position (up to 6000 uu), a velocity (up to 6000
 * uu/s), an angular velocity (6 rad/s) and a dot product (1) into the same
 * input vector. The first layer's weights all start at the same magnitude, so
 * the position components alone contribute ~1000x the activation of the dot
 * products and dominate the gradient completely: the small features are never
 * learned from, and the large ones saturate tanh on the first step.
 *
 * Every field below therefore names a divisor in `OBS_SCALES`, chosen so the
 * component lands in roughly [-1, 1]. `OBS_FIELDS` records the divisor AND a
 * hard bound per field, and `test/rl/obs.test.ts` asserts the bound holds over
 * randomized world states — so the normalization is a checked property, not a
 * comment.
 *
 * ===========================================================================
 * 2. TEAM 1 SEES A MIRRORED WORLD
 * ===========================================================================
 *
 * Blue (team 0) defends -Y and attacks +Y; orange (team 1) is the reverse.
 * Handed the raw world, a single network would have to learn "drive toward +Y"
 * and "drive toward -Y" as two unrelated skills, each from half the data — two
 * policies sharing one set of weights.
 *
 * So for team 1 the whole world is rotated 180 degrees about the Z axis before
 * it is written out:
 *
 *     (x, y, z) -> (-x, -y, z)
 *
 * That map is a proper rotation (determinant +1), which is exactly why it is
 * the right one: positions, velocities, angular velocities and orientation
 * columns all transform the same way, no handedness is flipped, and every dot
 * product, distance and cross product in the observation is unchanged. In the
 * mirrored frame EVERY agent defends -Y and attacks +Y.
 *
 * `test/rl/obs.test.ts` builds a blue state and its exact mirror image with the
 * teams swapped and asserts the two observations are bit-identical.
 *
 * ===========================================================================
 * 3. WHAT IS AND IS NOT INCLUDED
 * ===========================================================================
 *
 * The other cars are given RELATIVE to the agent (position, velocity) rather
 * than absolutely. Nothing is lost: the agent's own absolute state is also in
 * the vector, and the first layer is linear, so the network can recover an
 * absolute opponent position as a weighted sum whenever that is what it wants.
 * Relative vectors are simply the form most of the useful features are already
 * in, and they cost half the width.
 *
 * The layout is 70 floats and matches `OBS_SIZE` in ./types exactly. A
 * checkpoint records that width; a mismatch must be a loud failure, not a
 * silently misread input vector.
 *
 * No DOM, no Three.js, no `World` import — this runs in a worker, and it is
 * fed a structural view that a `World` satisfies.
 */

import { GOAL_CENTER } from '../physics/arena-data';
import type { BallState } from '../physics/ball';
import type { CarState } from '../physics/car-state';
import {
  ARENA,
  BALL_MAX_ANGULAR_SPEED,
  BALL_MAX_SPEED,
  BOOST_MAX,
  CAR_MAX_ANGULAR_SPEED,
  CAR_MAX_SPEED,
} from '../physics/constants';
import { OBS_SIZE, type Observation, type ObservationSpec } from './types';

// ---------------------------------------------------------------------------
// Scales
// ---------------------------------------------------------------------------

/**
 * Longest straight line in the arena INCLUDING the goal interiors, corner to
 * opposite corner. Every distance and every relative position divides by this,
 * so those components cannot leave [-1, 1] however the world is arranged.
 */
export const ARENA_SPAN = Math.hypot(2 * ARENA.halfWidth, 2 * ARENA.backNetY, ARENA.ceiling);

/**
 * The divisor for every field, by role. Nothing here is a physics constant in
 * its own right — each entry is a physics constant chosen as a normalizer, and
 * the import makes that traceable.
 */
export const OBS_SCALES = {
  /** Positions are scaled per axis; the arena is not cubic. */
  positionX: ARENA.halfWidth,
  positionY: ARENA.halfLength,
  positionZ: ARENA.ceiling,
  carVelocity: CAR_MAX_SPEED,
  carAngularVelocity: CAR_MAX_ANGULAR_SPEED,
  ballVelocity: BALL_MAX_SPEED,
  ballAngularVelocity: BALL_MAX_ANGULAR_SPEED,
  boost: BOOST_MAX,
  /** Orientation columns and dot products are already unit-scale. */
  axis: 1,
  /** Booleans are written as 0 or 1. */
  flag: 1,
  distance: ARENA_SPAN,
  relativePosition: ARENA_SPAN,
  /** Ball vs car: the two speed caps add. */
  ballRelativeVelocity: BALL_MAX_SPEED + CAR_MAX_SPEED,
  /** Car vs car. */
  carRelativeVelocity: 2 * CAR_MAX_SPEED,
  closingSpeed: BALL_MAX_SPEED + CAR_MAX_SPEED,
} as const;

const POSITION_SCALE: readonly [number, number, number] = [
  OBS_SCALES.positionX,
  OBS_SCALES.positionY,
  OBS_SCALES.positionZ,
];
const uniform3 = (s: number): readonly [number, number, number] => [s, s, s];

const CAR_VELOCITY_SCALE = uniform3(OBS_SCALES.carVelocity);
const CAR_ANGULAR_SCALE = uniform3(OBS_SCALES.carAngularVelocity);
const BALL_VELOCITY_SCALE = uniform3(OBS_SCALES.ballVelocity);
const BALL_ANGULAR_SCALE = uniform3(OBS_SCALES.ballAngularVelocity);
const AXIS_SCALE = uniform3(OBS_SCALES.axis);
const RELATIVE_POSITION_SCALE = uniform3(OBS_SCALES.relativePosition);
const BALL_RELATIVE_VELOCITY_SCALE = uniform3(OBS_SCALES.ballRelativeVelocity);
const CAR_RELATIVE_VELOCITY_SCALE = uniform3(OBS_SCALES.carRelativeVelocity);

/**
 * Slack on every bound. Positions are clamped by the physics to their limits
 * exactly, and a component sitting on 1.0 must not fail a `<=` by one ulp.
 */
const SLACK = 1e-3;

/**
 * A position's Y bound is the back of the net (a ball IS legitimately at
 * |y| = 6000 while it is in the goal) over the goal-line normalizer.
 */
const POSITION_BOUND = ARENA.backNetY / ARENA.halfLength + SLACK;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export type ObsFieldName =
  | 'selfPosition'
  | 'selfVelocity'
  | 'selfAngularVelocity'
  | 'selfForward'
  | 'selfUp'
  | 'selfBoost'
  | 'selfOnGround'
  | 'selfHasFlip'
  | 'selfSupersonic'
  | 'ballPosition'
  | 'ballVelocity'
  | 'ballAngularVelocity'
  | 'ballRelativePosition'
  | 'ballRelativeVelocity'
  | 'ballToOpponentGoal'
  | 'ballToOwnGoal'
  | 'opponentRelativePosition'
  | 'opponentRelativeVelocity'
  | 'opponentForward'
  | 'opponentBoost'
  | 'opponentOnGround'
  | 'opponentPresent'
  | 'teammateRelativePosition'
  | 'teammateRelativeVelocity'
  | 'teammateForward'
  | 'teammateBoost'
  | 'teammateOnGround'
  | 'teammatePresent'
  | 'distanceToBall'
  | 'ballDistanceToOpponentGoal'
  | 'ballDistanceToOwnGoal'
  | 'closingSpeedToBall'
  | 'forwardAlignmentToBall'
  | 'betweenBallAndOwnGoal';

/** A field, its offset, its divisor and the magnitude it may never exceed. */
export interface ObservationField {
  readonly name: ObsFieldName;
  readonly offset: number;
  readonly size: number;
  /** One divisor per component. */
  readonly scale: readonly number[];
  /** Max |value| any component of this field may take, post-scaling. */
  readonly bound: number;
}

interface FieldDef {
  name: ObsFieldName;
  size: number;
  scale: readonly number[];
  bound: number;
}

/**
 * THE LAYOUT, in order. Offsets are derived from it, so the table and the
 * writer below cannot drift: `OFFSET.selfVelocity` is computed here, not
 * written twice.
 */
const FIELD_DEFS: readonly FieldDef[] = [
  // --- the agent's own car -------------------------------------------------
  { name: 'selfPosition', size: 3, scale: POSITION_SCALE, bound: POSITION_BOUND },
  { name: 'selfVelocity', size: 3, scale: CAR_VELOCITY_SCALE, bound: 1 + SLACK },
  { name: 'selfAngularVelocity', size: 3, scale: CAR_ANGULAR_SCALE, bound: 1 + SLACK },
  { name: 'selfForward', size: 3, scale: AXIS_SCALE, bound: 1 + SLACK },
  { name: 'selfUp', size: 3, scale: AXIS_SCALE, bound: 1 + SLACK },
  { name: 'selfBoost', size: 1, scale: [OBS_SCALES.boost], bound: 1 + SLACK },
  { name: 'selfOnGround', size: 1, scale: [OBS_SCALES.flag], bound: 1 },
  { name: 'selfHasFlip', size: 1, scale: [OBS_SCALES.flag], bound: 1 },
  { name: 'selfSupersonic', size: 1, scale: [OBS_SCALES.flag], bound: 1 },

  // --- the ball ------------------------------------------------------------
  { name: 'ballPosition', size: 3, scale: POSITION_SCALE, bound: POSITION_BOUND },
  { name: 'ballVelocity', size: 3, scale: BALL_VELOCITY_SCALE, bound: 1 + SLACK },
  { name: 'ballAngularVelocity', size: 3, scale: BALL_ANGULAR_SCALE, bound: 1 + SLACK },

  // --- the ball, relative ---------------------------------------------------
  { name: 'ballRelativePosition', size: 3, scale: RELATIVE_POSITION_SCALE, bound: 1 + SLACK },
  {
    name: 'ballRelativeVelocity',
    size: 3,
    scale: BALL_RELATIVE_VELOCITY_SCALE,
    bound: 1 + SLACK,
  },
  { name: 'ballToOpponentGoal', size: 3, scale: RELATIVE_POSITION_SCALE, bound: 1 + SLACK },
  { name: 'ballToOwnGoal', size: 3, scale: RELATIVE_POSITION_SCALE, bound: 1 + SLACK },

  // --- nearest live opponent ------------------------------------------------
  { name: 'opponentRelativePosition', size: 3, scale: RELATIVE_POSITION_SCALE, bound: 1 + SLACK },
  {
    name: 'opponentRelativeVelocity',
    size: 3,
    scale: CAR_RELATIVE_VELOCITY_SCALE,
    bound: 1 + SLACK,
  },
  { name: 'opponentForward', size: 3, scale: AXIS_SCALE, bound: 1 + SLACK },
  { name: 'opponentBoost', size: 1, scale: [OBS_SCALES.boost], bound: 1 + SLACK },
  { name: 'opponentOnGround', size: 1, scale: [OBS_SCALES.flag], bound: 1 },
  { name: 'opponentPresent', size: 1, scale: [OBS_SCALES.flag], bound: 1 },

  // --- nearest live teammate ------------------------------------------------
  { name: 'teammateRelativePosition', size: 3, scale: RELATIVE_POSITION_SCALE, bound: 1 + SLACK },
  {
    name: 'teammateRelativeVelocity',
    size: 3,
    scale: CAR_RELATIVE_VELOCITY_SCALE,
    bound: 1 + SLACK,
  },
  { name: 'teammateForward', size: 3, scale: AXIS_SCALE, bound: 1 + SLACK },
  { name: 'teammateBoost', size: 1, scale: [OBS_SCALES.boost], bound: 1 + SLACK },
  { name: 'teammateOnGround', size: 1, scale: [OBS_SCALES.flag], bound: 1 },
  { name: 'teammatePresent', size: 1, scale: [OBS_SCALES.flag], bound: 1 },

  // --- scalars the network would otherwise have to learn to build -----------
  { name: 'distanceToBall', size: 1, scale: [OBS_SCALES.distance], bound: 1 + SLACK },
  { name: 'ballDistanceToOpponentGoal', size: 1, scale: [OBS_SCALES.distance], bound: 1 + SLACK },
  { name: 'ballDistanceToOwnGoal', size: 1, scale: [OBS_SCALES.distance], bound: 1 + SLACK },
  { name: 'closingSpeedToBall', size: 1, scale: [OBS_SCALES.closingSpeed], bound: 1 + SLACK },
  { name: 'forwardAlignmentToBall', size: 1, scale: [OBS_SCALES.axis], bound: 1 + SLACK },
  { name: 'betweenBallAndOwnGoal', size: 1, scale: [OBS_SCALES.flag], bound: 1 },
];

/** Every field with its resolved offset, in layout order. */
export const OBS_FIELDS: readonly ObservationField[] = (() => {
  const out: ObservationField[] = [];
  let offset = 0;
  for (const def of FIELD_DEFS) {
    out.push({ name: def.name, offset, size: def.size, scale: def.scale, bound: def.bound });
    offset += def.size;
  }
  return out;
})();

/** Width the layout above actually produces. Must equal `OBS_SIZE`. */
export const OBS_LAYOUT_SIZE = OBS_FIELDS.reduce((n, f) => n + f.size, 0);

/** Field name -> offset. The writer indexes through this, never a literal. */
export const OFFSET: Readonly<Record<ObsFieldName, number>> = (() => {
  const map = {} as Record<ObsFieldName, number>;
  for (const field of OBS_FIELDS) map[field.name] = field.offset;
  return map;
})();

/** The spec in the shape ./types declares. */
export const OBS_SPEC: ObservationSpec = { fields: OBS_FIELDS, size: OBS_LAYOUT_SIZE };

// ---------------------------------------------------------------------------
// Building
// ---------------------------------------------------------------------------

/** What the builder needs. A `World` satisfies it structurally. */
export interface ObsWorldView {
  readonly ball: BallState;
  readonly cars: readonly CarState[];
}

/**
 * The X/Y sign flip that puts a team-1 agent into the team-0 frame. See §2.
 * Team 0 gets +1 (identity), team 1 gets -1.
 */
export const mirrorSign = (team: 0 | 1): number => (team === 0 ? 1 : -1);

/** Write a mirrored, per-axis-scaled vector. `m` flips X and Y only. */
const put3 = (
  out: Observation,
  at: number,
  x: number,
  y: number,
  z: number,
  scale: readonly number[],
  m: number
): void => {
  out[at] = (x * m) / scale[0];
  out[at + 1] = (y * m) / scale[1];
  out[at + 2] = z / scale[2];
};

/** Allocate an observation buffer of the declared width. */
export const makeObservation = (): Observation => new Float32Array(OBS_SIZE);

/**
 * Index of the nearest non-demolished car on `team`, excluding `exclude`, or -1.
 *
 * Demolished cars are skipped: one is not a threat, cannot be passed to, and
 * has a stale position. Ties break to the lower index, so the choice is a pure
 * function of the state and the observation stays deterministic.
 */
const nearestCar = (
  cars: readonly CarState[],
  from: CarState,
  team: 0 | 1,
  exclude: number
): number => {
  let best = -1;
  let bestSq = Number.POSITIVE_INFINITY;
  for (let i = 0; i < cars.length; i++) {
    if (i === exclude) continue;
    const other = cars[i];
    if (other.team !== team || other.demolished) continue;
    const dx = other.position.x - from.position.x;
    const dy = other.position.y - from.position.y;
    const dz = other.position.z - from.position.z;
    const d = dx * dx + dy * dy + dz * dz;
    if (d < bestSq) {
      bestSq = d;
      best = i;
    }
  }
  return best;
};

/**
 * Write the observation for `carIndex` into `out` (allocated if omitted).
 *
 * Reads the orientation matrix's columns by index rather than through
 * `col()`: this runs once per agent per env step in the training loop and the
 * four Vec3 allocations `col` would make are pure garbage.
 */
export const buildObservation = (
  view: ObsWorldView,
  carIndex: number,
  out: Observation = makeObservation()
): Observation => {
  const car = view.cars[carIndex];
  const ball = view.ball;
  const team = car.team;
  const m = mirrorSign(team);

  const oppGoal = GOAL_CENTER[team === 0 ? 1 : 0];
  const ownGoal = GOAL_CENTER[team];

  // --- own car -------------------------------------------------------------
  const o = car.orientation;
  put3(out, OFFSET.selfPosition, car.position.x, car.position.y, car.position.z, POSITION_SCALE, m);
  put3(
    out,
    OFFSET.selfVelocity,
    car.velocity.x,
    car.velocity.y,
    car.velocity.z,
    CAR_VELOCITY_SCALE,
    m
  );
  put3(
    out,
    OFFSET.selfAngularVelocity,
    car.angularVelocity.x,
    car.angularVelocity.y,
    car.angularVelocity.z,
    CAR_ANGULAR_SCALE,
    m
  );
  // Columns of a row-major 3x3: forward = col 0, up = col 2.
  put3(out, OFFSET.selfForward, o[0], o[3], o[6], AXIS_SCALE, m);
  put3(out, OFFSET.selfUp, o[2], o[5], o[8], AXIS_SCALE, m);
  out[OFFSET.selfBoost] = car.boostAmount / OBS_SCALES.boost;
  out[OFFSET.selfOnGround] = car.onGround ? 1 : 0;
  out[OFFSET.selfHasFlip] = car.hasFlip ? 1 : 0;
  out[OFFSET.selfSupersonic] = car.supersonic ? 1 : 0;

  // --- ball ----------------------------------------------------------------
  put3(
    out,
    OFFSET.ballPosition,
    ball.position.x,
    ball.position.y,
    ball.position.z,
    POSITION_SCALE,
    m
  );
  put3(
    out,
    OFFSET.ballVelocity,
    ball.velocity.x,
    ball.velocity.y,
    ball.velocity.z,
    BALL_VELOCITY_SCALE,
    m
  );
  put3(
    out,
    OFFSET.ballAngularVelocity,
    ball.angularVelocity.x,
    ball.angularVelocity.y,
    ball.angularVelocity.z,
    BALL_ANGULAR_SCALE,
    m
  );

  // --- ball, relative -------------------------------------------------------
  const bx = ball.position.x - car.position.x;
  const by = ball.position.y - car.position.y;
  const bz = ball.position.z - car.position.z;
  put3(out, OFFSET.ballRelativePosition, bx, by, bz, RELATIVE_POSITION_SCALE, m);

  const bvx = ball.velocity.x - car.velocity.x;
  const bvy = ball.velocity.y - car.velocity.y;
  const bvz = ball.velocity.z - car.velocity.z;
  put3(out, OFFSET.ballRelativeVelocity, bvx, bvy, bvz, BALL_RELATIVE_VELOCITY_SCALE, m);

  const ogx = oppGoal.x - ball.position.x;
  const ogy = oppGoal.y - ball.position.y;
  const ogz = oppGoal.z - ball.position.z;
  put3(out, OFFSET.ballToOpponentGoal, ogx, ogy, ogz, RELATIVE_POSITION_SCALE, m);

  const wgx = ownGoal.x - ball.position.x;
  const wgy = ownGoal.y - ball.position.y;
  const wgz = ownGoal.z - ball.position.z;
  put3(out, OFFSET.ballToOwnGoal, wgx, wgy, wgz, RELATIVE_POSITION_SCALE, m);

  // --- nearest opponent and teammate ---------------------------------------
  writeOtherCar(
    out,
    OFFSET.opponentRelativePosition,
    view.cars,
    car,
    nearestCar(view.cars, car, team === 0 ? 1 : 0, carIndex),
    m
  );
  writeOtherCar(
    out,
    OFFSET.teammateRelativePosition,
    view.cars,
    car,
    nearestCar(view.cars, car, team, carIndex),
    m
  );

  // --- scalars --------------------------------------------------------------
  const ballDistance = Math.sqrt(bx * bx + by * by + bz * bz);
  out[OFFSET.distanceToBall] = ballDistance / OBS_SCALES.distance;
  out[OFFSET.ballDistanceToOpponentGoal] =
    Math.sqrt(ogx * ogx + ogy * ogy + ogz * ogz) / OBS_SCALES.distance;
  out[OFFSET.ballDistanceToOwnGoal] =
    Math.sqrt(wgx * wgx + wgy * wgy + wgz * wgz) / OBS_SCALES.distance;

  // Unit vector from the car toward the ball. Both scalars below are mirror
  // invariant (a dot of two mirrored vectors), so no `m` appears in them.
  const inv = ballDistance > 1e-6 ? 1 / ballDistance : 0;
  const ux = bx * inv;
  const uy = by * inv;
  const uz = bz * inv;

  // Positive while the gap is closing: the car's velocity minus the ball's,
  // projected onto the direction to the ball.
  out[OFFSET.closingSpeedToBall] =
    (-(bvx * ux) - bvy * uy - bvz * uz) / OBS_SCALES.closingSpeed;
  out[OFFSET.forwardAlignmentToBall] = o[0] * ux + o[3] * uy + o[6] * uz;

  // In the mirrored frame every agent's own goal is at -Y, so "goal side of the
  // ball" is simply a smaller Y than the ball's.
  out[OFFSET.betweenBallAndOwnGoal] = car.position.y * m < ball.position.y * m ? 1 : 0;

  return out;
};

/**
 * One other-car block: relative position, relative velocity, forward axis,
 * boost, ground contact, and whether the block holds anything at all.
 *
 * An absent car (no teammate in 1v1, every opponent demolished) writes zeros
 * with `present = 0` rather than a sentinel position. Zeros are the network's
 * "no contribution" input, and the presence flag is what lets it tell a car at
 * the origin from no car.
 */
const writeOtherCar = (
  out: Observation,
  base: number,
  cars: readonly CarState[],
  self: CarState,
  index: number,
  m: number
): void => {
  if (index < 0) {
    for (let i = 0; i < 12; i++) out[base + i] = 0;
    return;
  }
  const other = cars[index];
  put3(
    out,
    base,
    other.position.x - self.position.x,
    other.position.y - self.position.y,
    other.position.z - self.position.z,
    RELATIVE_POSITION_SCALE,
    m
  );
  put3(
    out,
    base + 3,
    other.velocity.x - self.velocity.x,
    other.velocity.y - self.velocity.y,
    other.velocity.z - self.velocity.z,
    CAR_RELATIVE_VELOCITY_SCALE,
    m
  );
  const o = other.orientation;
  put3(out, base + 6, o[0], o[3], o[6], AXIS_SCALE, m);
  out[base + 9] = other.boostAmount / OBS_SCALES.boost;
  out[base + 10] = other.onGround ? 1 : 0;
  out[base + 11] = 1;
};
